import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import CameraMarker from './CameraMarker'
import SubjectMarker from './SubjectMarker'
import AngleMarkers from './AngleMarkers'
import Angle from './Angle'
import PpfHoverPreview from './PpfHoverPreview'
import IrPreview from './IrPreview'
import CameraView from './CameraView'
import AnalyticZones from './CameraView/AnalyticZones'
import ClippedView from './CameraView/ClippedView'
import convertUnitFromMap from 'helpers/convertUnitFromMap'
import showContextMenu from 'helpers/showContextMenu'
import RenameCameraOverlay from 'components/Camera/RenameCameraOverlay'
import { CONVENTIONAL, CAMERA_COLORS, CAMERA_SELECTED_COLORS, COPY_ICON_STACK } from 'helpers/globals'
import { zoomToSingleCameraAction, addMapComponentReferencesAction, getStreetviewAction } from 'actions/mapActions'
import { batchUpdateCalculatorAction, updateCalculatorAction } from 'actions/calculatorActions'
import { destroyCameraAction, changeCameraAction, updatePPFHoverAction } from 'actions/cameraActions'
import { updateCurrentEntityAction, checkOfflineLimitsAction } from 'actions/applicationActions'
import { messageAction } from 'actions/messageActions'
import ContextMenu from 'ContextMenu'
import modelAovType from 'helpers/modelAovType'
import { makeCameraPropSelector, makeMapConvertedCameraPropSelector, makeMapRefSelector, makeMapTypeSelector,
  makeCameraCountSelector, makeCameraSelectedSelector, makeUiPropSelector, makeUserSettingsPropSelector,
  makePpfZoneColorsSelector } from 'components/MultiImager/selectors'
import { wallsLocationsSelector, makeDeviceWireSelectedSelector } from 'selectors/components'
import calculateVertex from './helpers/calculateVertex'
import onMobile from 'helpers/onMobile'

const makeMapStateToProps = _ => {
  const horizontalAngleOfViewSelector = makeCameraPropSelector('horizontalAngleOfView')
  const locationSelector = makeCameraPropSelector('location')
  const headingSelector = makeCameraPropSelector('heading')
  const verticalAngleOfViewSelector = makeCameraPropSelector('verticalAngleOfView')
  const fovModeSelector = makeCameraPropSelector('fovMode')
  const markersSelector = makeCameraPropSelector('markers')
  const activeSelector = makeCameraPropSelector('active')
  const tiltSelector = makeCameraPropSelector('tilt')
  const lockedSelector = makeCameraPropSelector('locked')
  const streetViewActiveSelector = makeCameraPropSelector('streetViewActive')
  const nameSelector = makeCameraPropSelector('name')
  const notesSelector = makeCameraPropSelector('notesSelector')
  const newlyAddedSelector = makeCameraPropSelector('newlyAdded')
  const streetViewUrlSelector = makeCameraPropSelector('streetViewUrl')
  const removeSelector = makeCameraPropSelector('remove')
  const modelSelector = makeCameraPropSelector('model')
  const userSelectedGeneralSceneSelector = makeCameraPropSelector('userSelectedGeneralScene')
  const streetViewAvailableSelector = makeCameraPropSelector('streetViewAvailable')
  const ppfHoverPositionSelector = makeCameraPropSelector('ppfHoverPosition')
  const displayIrLineSelector = makeCameraPropSelector('displayIrLine')
  const distanceSelector = makeMapConvertedCameraPropSelector('distance')
  const cameraHeightSelector = makeMapConvertedCameraPropSelector('cameraHeight')
  const colorSelector = makeCameraPropSelector('color')
  const isFixedImagerSelector = makeCameraPropSelector('isFixedImager')
  const mapRefSelector = makeMapRefSelector()
  const mapTypeSelector = makeMapTypeSelector()
  const cameraCountSelector = makeCameraCountSelector()
  const cameraSelectedSelector = makeCameraSelectedSelector()
  const ppfHoverSelector = makeUiPropSelector('ppfHover')
  const showCameraBlindspotSelector = makeUiPropSelector('showCameraBlindspot')
  const horizontalResolutionSelector = makeCameraPropSelector('horizontalResolution')
  const verticalResolutionSelector = makeCameraPropSelector('verticalResolution')
  const ppfSelector = makeCameraPropSelector('ppf')
  const showAnalyticZonesSelector = makeUiPropSelector('showAnalyticZones')
  const cameraViewsChoiceSelector = makeUiPropSelector('cameraViewsChoice')
  const showSimpleCameraIconSelector = makeUiPropSelector('showSimpleCameraIcon')
  const showWallBlindspotsSelector = makeUiPropSelector('showWallBlindspots')
  const mapLoadedSelector = makeUiPropSelector('mapLoaded')
  const drawingWallSelector = makeUiPropSelector('drawingWall')
  const drawingWireSelector = makeUiPropSelector('drawingWire')
  const isImperialSelector = makeUiPropSelector('isImperial')
  const ppfZoneBordersSelector = makeUserSettingsPropSelector('ppfZoneBorders')
  const ppfZoneColorOverrideSelector = makeUserSettingsPropSelector('ppfZoneColorOverride')
  const ppfZoneColorsSelector = makePpfZoneColorsSelector()
  const wireSelectedSelector = makeDeviceWireSelectedSelector()

  const mapStateToProps = (state, ownProps) => ({
    id: ownProps.id,
    horizontalAngleOfView: horizontalAngleOfViewSelector(state, ownProps),
    location: locationSelector(state, ownProps),
    heading: headingSelector(state, ownProps),
    verticalAngleOfView: verticalAngleOfViewSelector(state, ownProps),
    fovMode: fovModeSelector(state, ownProps),
    markers: markersSelector(state, ownProps),
    active: activeSelector(state, ownProps),
    tilt: tiltSelector(state, ownProps),
    locked: lockedSelector(state, ownProps),
    streetViewActive: streetViewActiveSelector(state, ownProps),
    name: nameSelector(state, ownProps),
    notes: notesSelector(state, ownProps),
    newlyAdded: newlyAddedSelector(state, ownProps),
    streetViewUrl: streetViewUrlSelector(state, ownProps),
    remove: removeSelector(state, ownProps),
    model: modelSelector(state, ownProps),
    userSelectedGeneralScene: userSelectedGeneralSceneSelector(state, ownProps),
    streetViewAvailable: streetViewAvailableSelector(state, ownProps),
    ppfHoverPosition: ppfHoverPositionSelector(state, ownProps),
    displayIrLine: displayIrLineSelector(state, ownProps),
    distance: distanceSelector(state, ownProps),
    cameraHeight: cameraHeightSelector(state, ownProps),
    color: colorSelector(state, ownProps),
    isFixedImager: isFixedImagerSelector(state, ownProps),
    map: mapRefSelector(state),
    mapType: mapTypeSelector(state),
    camerasCount: cameraCountSelector(state),
    activeInUI: cameraSelectedSelector(state, ownProps),
    ppfHover: ppfHoverSelector(state),
    showCameraBlindspot: showCameraBlindspotSelector(state),
    horizontalResolution: horizontalResolutionSelector(state, ownProps),
    verticalResolution: verticalResolutionSelector(state, ownProps),
    ppf: ppfSelector(state, ownProps),
    showAnalyticZones: showAnalyticZonesSelector(state),
    cameraViewsChoice: cameraViewsChoiceSelector(state),
    showSimpleCameraIcon: showSimpleCameraIconSelector(state),
    showWallBlindspots: showWallBlindspotsSelector(state),
    walls: wallsLocationsSelector(state),
    mapLoaded: mapLoadedSelector(state),
    drawingWall: drawingWallSelector(state),
    drawingWire: drawingWireSelector(state),
    isImperial: isImperialSelector(state),
    ppfZoneBorders: ppfZoneBordersSelector(state),
    ppfZoneColorOverride: ppfZoneColorOverrideSelector(state),
    ppfZoneColors: ppfZoneColorsSelector(state, ownProps),
    wireSelected: wireSelectedSelector(state, { id: ownProps.id, objType: 'camera' })
  })
  return mapStateToProps
}

class Camera extends React.Component {
  constructor(props) {
    super(props)
    const { props: { fovMode, verticalAngleOfView, horizontalAngleOfView, distance, heading, location, map } } = this
    const latLngLocation = new google.maps.LatLng(location)
    const cameraMarkerPosition = isNaN(latLngLocation.lat()) ? map.getCenter() : latLngLocation
    const subjectMarkerPosition = google.maps.geometry.spherical.computeOffset(cameraMarkerPosition, distance, heading)
    const angle = fovMode === CONVENTIONAL ? horizontalAngleOfView : verticalAngleOfView

    this.state = {
      paths: calculateVertex({ angle, distance, cameraMarkerPosition, heading }),
      angle: horizontalAngleOfView,
      distance,
      cameraMarkerPosition,
      subjectMarkerPosition,
      heading,
      subjectMarkerDragging: false,
      angleMarkerDragging: false,
      cameraDragging: false
    }
    this.streetViewService = new google.maps.StreetViewService()
    this.markers = {}
  }

  componentDidMount() {
    const { remove, props: { id, updateCalculatorAction, location, addMapComponentReferencesAction,
      newlyAdded, streetViewUrl, map, classes } } = this

    this.contextMenu = new ContextMenu(map, this.contextMenuOptions(classes))

    this.markers.camera.addListener('contextmenu', showContextMenu(this.contextMenu))
    google.maps.event.addListener(this.contextMenu, 'menu_item_selected', this.eventHandler)

    google.maps.event.addDomListener(this.markers.camera, 'dblclick', this.showRenameCameraOverlay)
    newlyAdded && !onMobile() && this.showRenameCameraOverlay({ autoclose: true })

    this.markers.camera.addListener('position_changed', this.move)
    this.markers.camera.addListener('mousedown', this.setCurrentIfApplicable)
    this.markers.subject.addListener('mousedown', this.createSubjectListeners)
    addMapComponentReferencesAction('cameras', id, { remove, markers: this.extractMarkersFromComponents() })
    if (!location) updateCalculatorAction(this.state.cameraMarkerPosition, 'location', id, true)
    if (!streetViewUrl && !EXPORT) this.processStreetView()
  }

  componentWillUnmount = _ => this.remove()

  processStreetView = _ => {
    this.streetViewService.getPanorama({ location: this.state.cameraMarkerPosition, radius: 20 }, this.getStreetView)
  }

  getStreetView = (_, status) => {
    const { state: { heading, cameraMarkerPosition }, props: { tilt, mapType, id, streetViewActive, streetViewAvailable, userSelectedGeneralScene } } = this
    this.props.getStreetviewAction({
      status, heading, cameraMarkerPosition, tilt, mapType, id, streetViewActive, streetViewAvailable, userSelectedGeneralScene
    })
  }

  setCurrentIfApplicable = _ => {
    const { drawingWall, drawingWire, active, camerasCount, activeInUI, changeCameraAction, updateCurrentEntityAction, id } = this.props
    if (drawingWall || drawingWire) return
    if (!activeInUI) updateCurrentEntityAction({ id, type: 'camera' })
    if (!active && camerasCount > 1) changeCameraAction(id)
  }

  extractMarkersFromComponents = _ =>
    Object.assign(
      {
        camera: this.markers.camera,
        cameraMarkerLockIcon: this.markers.lockIcon,
        subject: this.markers.subject,
        leftAngle: this.markers.leftAngle,
        rightAngle: this.markers.rightAngle,
        polygon: this.markers.polygon,
        circle: this.markers.circle,
        ppfHoverPreviewOverlay: this.markers.ppfHoverPreviewOverlay,
        irPreviewLine: this.markers.irLine,
        PTZcircle: this.markers.PTZcircle
      },
      this.markers.cameraView && { cameraView: this.markers.cameraView },
      this.markers.analyticZones && { analyticZones: this.markers.analyticZones },
      this.markers.clippedView && { clippedView: this.markers.clippedView },
      this.markers.cameraOutline && { cameraOutline: this.markers.cameraOutline }
    )

  contextMenuOptions(classes) {
    const menuItemBuilder = function() {
      const { props: { locked } } = this
      const items = [
        {
          className: classes.contextMenuItem,
          eventName: 'renameCamera',
          id: 'menu_item_rename_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon='pen' />)} Rename Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'goToCamera',
          id: 'menu_item_go_to_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={['far', 'dot-circle']} />)} Go To Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'duplicateCamera',
          id: 'menu_item_duplicate_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(COPY_ICON_STACK)} Duplicate Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'lockCamera',
          id: 'menu_item_lock_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={locked ? 'unlock' : 'lock'} />)} ${(locked ? 'Unlock' : 'Lock')} Camera`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'deleteCamera',
          id: 'menu_item_delete_camera',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={['far', 'trash-alt']} />)} Delete Camera`
        }
      ]

      return items
    }
    return {
      classNames: {
        menu: classes.contextMenu,
        menuSeparator: classes.contextMenuSeparator
      },
      menuItems: menuItemBuilder.bind(this)
    }
  }

  eventHandler = (_, eventName) => {
    const { props: { locked, id, horizontalAngleOfView } } = this
    switch (eventName) {
      case 'renameCamera':
        this.showRenameCameraOverlay()
        break
      case 'goToCamera':
        const camera = { horizontalAngleOfView, markers: this.extractMarkersFromComponents() }
        this.props.zoomToSingleCameraAction(camera)
        break
      case 'lockCamera':
        this.props.updateCalculatorAction(!locked, 'locked', id)
        break
      case 'deleteCamera':
        if (locked) {
          this.props.messageAction('generalError', 'Cannot delete locked camera. Right click to unlock.')
        }
        else if (window.confirm(`Are you sure you want to delete ${this.props.name}?`)) {
          this.props.destroyCameraAction(id)
        }
        break
      case 'duplicateCamera':
        this.props.checkOfflineLimitsAction('copyCamera', [id])
        break
    }
    return this.contextMenu.hide()
  }

  showRenameCameraOverlay = ({ autoclose } = {}) => {
    const { map, id, name } = this.props
    new RenameCameraOverlay({
      map,
      initialTitle: name,
      markerPosition: this.markers.camera.getPosition(),
      autoClose: autoclose || false,
      callback: name => this.props.updateCalculatorAction(name, 'name', id, true)
    })
  }

  createSubjectListeners = _ => {
    this.setState({ subjectMarkerDragging: true })
    google.maps.event.addListener(this.markers.subject, 'position_changed', this.update)

    if (this.props.isLens && this.props.isFixedImager) this.props.setDragging(true)

    const component = this
    google.maps.event.addListener(this.markers.subject, 'mouseup', function() {
      google.maps.event.clearListeners(component.markers.subject, 'position_changed')
      const distance = google.maps.geometry.spherical.computeDistanceBetween(component.state.cameraMarkerPosition, component.state.subjectMarkerPosition)
      component.setState({ subjectMarkerDragging: false, distance })

      if (component.props.isLens && component.props.isFixedImager) component.props.setDragging(false)
    })
  }

  move = _ => {
    const { props: { distance, heading }, state: { angle } } = this
    const cameraMarkerPosition = this.markers.camera.getPosition()
    const subjectMarkerPosition = google.maps.geometry.spherical.computeOffset(cameraMarkerPosition, distance, heading)
    this.setState({
      paths: calculateVertex({ angle, distance, cameraMarkerPosition, heading }),
      heading,
      cameraMarkerPosition,
      subjectMarkerPosition
    })
  }

  update = _ => {
    const cameraMarkerPosition = this.markers.camera.getPosition()
    const subjectMarkerPosition = this.markers.subject.getPosition()
    const heading = google.maps.geometry.spherical.computeHeading(cameraMarkerPosition, subjectMarkerPosition)
    const distance = google.maps.geometry.spherical.computeDistanceBetween(cameraMarkerPosition, subjectMarkerPosition)
    const { props: { isLens, isFixedImager, isImperial, updateLensHeadings }, state: { angle } } = this

    this.setState({
      paths: calculateVertex({ angle, distance, cameraMarkerPosition, heading }),
      distance,
      subjectMarkerPosition,
      heading
    })

    const convertedDistance = `${convertUnitFromMap({ value: distance, isImperial })}`

    this.props.batchUpdateCalculatorAction(
      { distance: convertedDistance, heading }, ['distance', 'heading'],
      this.props.id, true
    )

    if (isLens && isFixedImager) updateLensHeadings(this.props.id, heading, convertedDistance)
  }

  updateAngle = angleOfView => {
    const angleToUpdate = this.props.fovMode === CONVENTIONAL ? 'horizontalAngleOfView' : 'verticalAngleOfView'
    const { state: { cameraMarkerPosition, heading, distance }, props: { id, batchUpdateCalculatorAction } } = this

    batchUpdateCalculatorAction({ [angleToUpdate]: `${angleOfView}` }, [angleToUpdate], id, true)
    this.setState({
      paths: calculateVertex({
        angle: angleOfView,
        distance,
        cameraMarkerPosition,
        heading
      }),
      angle: angleOfView
    })
  }

  calculateHeadingAfterSubjectMarkerMove = _ =>
    this.props.updateCalculatorAction(
      google.maps.geometry.spherical.computeHeading(this.markers.camera.getPosition(), this.markers.subject.getPosition()),
      'heading',
      this.props.id,
      true
    )

  componentWillReceiveProps(nextProps) {
    if (this.state.distance !== nextProps.distance) this.setState({ distance: nextProps.distance })
    if (!this.state.subjectMarkerDragging && this.diffProps(nextProps)) {
      const angle = nextProps.fovMode === CONVENTIONAL ? nextProps.horizontalAngleOfView : nextProps.verticalAngleOfView

      const latLng = new google.maps.LatLng(nextProps.location)
      const cameraMarkerPosition = isNaN(latLng.lat()) ? this.state.cameraMarkerPosition : latLng

      const subjectMarkerPosition = google.maps.geometry.spherical.computeOffset(cameraMarkerPosition, nextProps.distance, nextProps.heading)
      this.setState({
        cameraMarkerPosition,
        paths: calculateVertex({
          distance: nextProps.distance,
          angle,
          cameraMarkerPosition,
          heading: nextProps.heading
        }),
        angle,
        subjectMarkerPosition: subjectMarkerPosition,
        heading: nextProps.heading
      })
    }

    if (nextProps.isLens && this.props.position !== nextProps.position) {
      const angle = nextProps.fovMode === CONVENTIONAL ? nextProps.horizontalAngleOfView : nextProps.verticalAngleOfView
      const { position, distance, heading } = nextProps
      const subjectMarkerPosition = google.maps.geometry.spherical.computeOffset(position, distance, heading)

      this.setState({
        paths: calculateVertex({ angle, distance, cameraMarkerPosition: position, heading }),
        heading,
        cameraMarkerPosition: position,
        subjectMarkerPosition
      })
    }
  }

  diffProps(nextProps) {
    const currentProps = this.props
    const keysToCompare = ['fovMode', 'horizontalAngleOfView', 'verticalAngleOfView', 'location', 'distance', 'heading']
    return keysToCompare.some(key => {
      switch (key) {
        case 'location':
          return JSON.stringify(nextProps.location) !== JSON.stringify(currentProps.location)
        default:
          return nextProps[key] !== currentProps[key]
      }
    })
  }

  remove = _ => {
    const markers = this.extractMarkersFromComponents()
    Object.values(markers)
      .filter(m => !!m)
      .forEach(m => Array.isArray(m) ? m.forEach(mm => !!mm && mm.setMap(null)) : m.setMap(null))
  }

  setAngleMarkerDragging = angleMarkerDragging => {
    if (this.state.angleMarkerDragging !== angleMarkerDragging) this.setState({ angleMarkerDragging })
  }

  setCameraDragging = cameraDragging => {
    if (this.state.cameraDragging !== cameraDragging) this.setState({ cameraDragging })
  }

  addToMarkers = (key, value) => this.markers[key] = value

  render() {
    const { setCurrentIfApplicable, addToMarkers, processStreetView, calculateHeadingAfterSubjectMarkerMove, updateAngle,
      setAngleMarkerDragging, setCameraDragging } = this
    const { cameraMarkerPosition, subjectMarkerPosition, heading, distance, paths, subjectMarkerDragging,
      angleMarkerDragging, cameraDragging } = this.state
    const { map, id, isImperial, horizontalAngleOfView, verticalAngleOfView, model, name, fovMode, color, locked, displayIrLine,
      showCameraBlindspot, ppfHoverPosition, ppfHover, activeInUI, tilt, cameraHeight, isLens, draggingOverride, messageAction,
      updatePPFHoverAction, updateCalculatorAction, horizontalResolution, verticalResolution, showAnalyticZones, ppf, cameraViewsChoice,
      showWallBlindspots, walls, mapLoaded, showSimpleCameraIcon, ppfZoneBorders, ppfZoneColorOverride, ppfZoneColors, drawingWire, wireSelected } = this.props

    const angle = fovMode === CONVENTIONAL ? horizontalAngleOfView : verticalAngleOfView
    const selectionOverride = this.props.selectionOverride || wireSelected
    const displayColor = activeInUI || selectionOverride || cameraViewsChoice !== 'All Cameras' ? CAMERA_SELECTED_COLORS[color] : CAMERA_COLORS[color]
    const showCameraComponents = cameraViewsChoice === 'All Cameras' || (cameraViewsChoice === 'Selected Camera' && (selectionOverride || activeInUI))

    return (
      <div>
        <CameraMarker { ...{ addToMarkers, position: cameraMarkerPosition, heading, map, color: displayColor, locked, name, id, isLens, drawingWire,
          updateCalculatorAction, processStreetView, setCameraDragging, messageAction, form: model && model.formFactor, showSimpleCameraIcon,
          fisheyeAov: model && model.is_fisheye && angle } } />
        <SubjectMarker { ...{ addToMarkers, map, position: subjectMarkerPosition, processStreetView, calculateHeadingAfterSubjectMarkerMove,
          visible: showCameraComponents && activeInUI && !drawingWire, locked } } />
        <AngleMarkers
          visible={showCameraComponents && activeInUI && !drawingWire && horizontalAngleOfView < 180 && modelAovType(model) !== 'fixed'}
          { ...{ angle, paths, map, addToMarkers, locked, setAngleMarkerDragging, updateAngle, model, fovMode, messageAction, distance, heading, cameraMarkerPosition } }
        />
        <Angle { ...{ model: model || {}, setCurrentIfApplicable, addToMarkers, map, paths, distance, horizontalAngleOfView, cameraMarkerPosition,
          updatePPFHoverAction, activeInUI, selectionOverride, color: displayColor, showCameraBlindspot, showAnalyticZones, showCameraComponents, showWallBlindspots } } />
        <CameraView { ...{ showCameraBlindspot, horizontalAngleOfView, verticalAngleOfView, map, color: displayColor, cameraHeight,
          setCurrentIfApplicable, tilt, distance, fovMode, addToMarkers, paths, cameraMarkerPosition, heading, subjectMarkerDragging,
          angleMarkerDragging, cameraDragging, isLens, draggingOverride, activeInUI, selectionOverride, showAnalyticZones,
          showCameraComponents, showWallBlindspots, walls, mapLoaded } } />
        <AnalyticZones { ...{ showAnalyticZones, horizontalAngleOfView, verticalAngleOfView, map, color: displayColor, cameraHeight, showCameraBlindspot,
          setCurrentIfApplicable, tilt, distance, fovMode, addToMarkers, paths, cameraMarkerPosition, heading, subjectMarkerDragging, angleMarkerDragging,
          cameraDragging, isLens, draggingOverride, activeInUI, selectionOverride, horizontalResolution, ppf, verticalResolution, showCameraComponents,
          showWallBlindspots, walls, subjectMarkerPosition, mapLoaded, ppfZoneBorders, ppfZoneColorOverride, ppfZoneColors } } />
        <ClippedView { ...{ fovMode, verticalAngleOfView, horizontalAngleOfView, paths, showWallBlindspots, walls, distance, heading, cameraMarkerPosition, map,
          color: displayColor, activeInUI, selectionOverride, addToMarkers, setCurrentIfApplicable, subjectMarkerDragging, angleMarkerDragging, cameraDragging,
          isLens, draggingOverride, showCameraBlindspot, showAnalyticZones, showCameraComponents, subjectMarkerPosition, mapLoaded } } />
        <PpfHoverPreview { ...{ addToMarkers, activeInUI: showCameraComponents && activeInUI && !drawingWire, map, ppfHoverPosition, ppf: ppfHover.ppf, isImperial } } />
        <IrPreview { ...{ addToMarkers, map, cameraMarkerPosition, heading, model, displayIrLine: showCameraComponents && !drawingWire && displayIrLine } } />
      </div>
    )
  }
}

export default connect(makeMapStateToProps, { batchUpdateCalculatorAction, updateCalculatorAction,
  zoomToSingleCameraAction, destroyCameraAction, changeCameraAction, addMapComponentReferencesAction,
  messageAction, updateCurrentEntityAction, updatePPFHoverAction, checkOfflineLimitsAction, getStreetviewAction })(injectSheet(style)(Camera))



// WEBPACK FOOTER //
// ./src/components/Camera/index.js